/**
 * Pure variable-reference rewriting over step/screen trees — extracted from
 * V2ClipboardVariablePortabilityService.rewriteStepVariableReferences (SPEC-030)
 * so the canvas clipboard AND the builder-service block-library instantiation
 * share ONE walk. No Firebase, no React.
 *
 * Rewritten reference channels (the authoritative set the clipboard walk owns):
 * 1. edge-condition guard keys — any `conditions[]` array whose entries carry a
 *    string `key`;
 * 2. direct id fields — `variableId`, `targetVariableId`, `emailVariableId`;
 * 3. `@ref` template references inside string values (id or slugified name).
 */

type AnyRecord = Record<string, unknown>;

export interface VariableReferenceRemapMapping {
  /** exact variable id (and ref alias) → replacement id */
  variableIdMapping: Map<string, string>;
  /** template `@ref` token → replacement ref token */
  variableRefMapping: Map<string, string>;
}

const TEMPLATE_REF_PATTERN = /@([A-Za-z0-9_-]+)((?:\.[A-Za-z0-9_-]+)*)/g;

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isRecord(value: unknown): value is AnyRecord {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function walkObject(node: unknown, visitor: (obj: AnyRecord) => void): void {
  if (Array.isArray(node)) {
    for (const item of node) {
      walkObject(item, visitor);
    }
    return;
  }

  if (!isRecord(node)) return;

  visitor(node);

  for (const value of Object.values(node)) {
    walkObject(value, visitor);
  }
}

export function replaceTemplateRefs(value: string, variableRefMapping: Map<string, string>): string {
  if (!value.includes('@') || variableRefMapping.size === 0) return value;
  return value.replace(TEMPLATE_REF_PATTERN, (match, ref: string, suffix: string = '') => {
    const replacement = variableRefMapping.get(ref);
    return replacement ? `@${replacement}${suffix}` : match;
  });
}

/**
 * Return deep-cloned steps with every variable reference rewritten through the
 * mappings. Steps without any matching reference come back structurally equal.
 */
export function remapVariableReferencesInSteps<T>(
  steps: T[],
  mapping: VariableReferenceRemapMapping,
): T[] {
  if (mapping.variableIdMapping.size === 0 && mapping.variableRefMapping.size === 0) {
    return steps;
  }

  return steps.map((step) => {
    const rewritten = deepClone(step);

    walkObject(rewritten, (obj) => {
      if (Array.isArray(obj.conditions)) {
        for (const condition of obj.conditions) {
          if (!isRecord(condition) || typeof condition.key !== 'string') continue;
          const nextKey = mapping.variableIdMapping.get(condition.key);
          if (nextKey) {
            condition.key = nextKey;
          }
        }
      }

      for (const [key, value] of Object.entries(obj)) {
        if (
          typeof value === 'string' &&
          (key === 'variableId' || key === 'targetVariableId' || key === 'emailVariableId') &&
          mapping.variableIdMapping.has(value)
        ) {
          obj[key] = mapping.variableIdMapping.get(value);
          continue;
        }

        if (typeof value === 'string') {
          const nextValue = replaceTemplateRefs(value, mapping.variableRefMapping);
          if (nextValue !== value) {
            obj[key] = nextValue;
          }
        }
      }
    });

    return rewritten;
  });
}
